import { Focusable } from '@ariakit/react'
import { format } from 'date-fns'
// TODO: Fix this auto-generated suppression.
// eslint-disable-next-line rulesdir/require-next-link-wrapper
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import pluralize from 'pluralize'
import { forwardRef, useCallback, useMemo, useState } from 'react'

import {
  ExpandableText,
  MediaThumbnail,
  SelectMark,
  Star,
  StarRating,
  ViewportSizes,
  useIsViewport,
} from '@syconium/little-miss-figgy'

import { NextDialogProvider } from '../../../app/_components/dialogs/NextDialog.client'
import { useFeatureFlag } from '../../../app/_providers/ExperimentationProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { useModalState } from '../../../components/modals/useModalState'
import * as Review from '../../../components/reviews/Review.styles'
import * as ReviewDialog from '../../../components/reviews/ReviewDetailsDialog/ReviewDetailsDialog.styles'
import {
  ReviewMediaTile,
  ReviewPhotosSection,
} from '../../../components/reviews/ReviewPhotosSection/ReviewPhotosSection'
import {
  type ReviewMedia,
  ReviewsSwiperMediaSlider,
} from '../../../components/reviews/ReviewsSwiperMediaSlider/ReviewsSwiperMediaSlider'
import { ReviewSummary } from '../../../components/reviews/ReviewSummary'
import { trackEvent } from '../../../lib/analytics'
import { useProductReviews } from '../../../lib/hooks/useProductReviews'
import { fieldsByFieldId } from '../../../lib/reviews/constants'
import {
  getFieldValue,
  getTranslatedValueFromReviewsFilter,
  isReviewMediaImage,
  isReviewsTranslatedFilterWithValuesKey,
} from '../../../lib/reviews/utils'
import { useProductReviewsHref, useProductWriteReviewHref } from '../../../lib/routing'
import { isBrowser } from '../../../lib/utils'
import { ProductReview, ProductReviewSet } from '../../../types/graphql'

import {
  ButtonText,
  ButtonWrapper,
  ButtonWrapperFooter,
  ButtonWrapperMobile,
  Divider,
  PaddedItem,
  ProductDetailsCopy,
  ReviewAverageScore,
  ReviewBackground,
  ReviewButton,
  ReviewCountText,
  ReviewCountTextContainerBody,
  ReviewHeader,
  ReviewItem,
  ReviewPhotosWrapper,
  ReviewRatingsContainer,
  ReviewStarIconContainer,
  ReviewStars,
  ReviewTitle,
  ReviewTitleButtonWrapper,
  ReviewWrapper,
} from './styles'

export interface IProps {
  handle: string
  colorKey: string | undefined | null
  shopifyProductId: string
}

export const ReviewsBody = forwardRef<HTMLDivElement, IProps>(
  ({ handle, colorKey, shopifyProductId }, ref) => {
    const {
      detailsSection: {
        reviewBaseOn,
        reviewPhotosTitle,
        reviewsCountNoun,
        reviewsTitle,
        reviewsEmptyTitle,
        seeAllReviewsCTA,
        writeReviewCTA,
        reviewStarRating,
      },
      magnolia: {
        reviewsPage: {
          readMore,
          readLess,
          filters: { filter: filterFixtures },
        },
        navigation,
      },
    } = useTranslation()

    const { productReviews } = useProductReviews({
      shopifyProductId,
    })

    const { productReviews: productReviewsWithMediaFilter } = useProductReviews({
      shopifyProductId,
      filters: { mediaType: ['photos'] },
    })

    const flag = useFeatureFlag<'true' | 'false'>({
      key: 'show-reviews-photo-carousel-by-default',
      controlVariant: 'false',
      ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
    })
    const isDefaultPhotosCarouselFlagEnabled = flag === 'true'
    const reviewsPhotoCarouselSource: ProductReviewSet | undefined = useMemo(
      () => (isDefaultPhotosCarouselFlagEnabled ? productReviewsWithMediaFilter : productReviews),
      [isDefaultPhotosCarouselFlagEnabled, productReviews, productReviewsWithMediaFilter]
    )

    // plain=true query param is used by iOS app to view webpages within a webview without the website's header/footer
    const plainQueryParamValue = useSearchParams()?.get('plain') ?? undefined
    const isSmallViewport = useIsViewport(ViewportSizes['small'])
    const reviewsMagnoliaViewPath = useProductReviewsHref({
      handle,
      colorKey,
      query: {
        plain: plainQueryParamValue,
      },
    })
    const reviewsMagnoliaWritePath = useProductWriteReviewHref({
      handle,
      colorKey,
      query: {
        plain: plainQueryParamValue,
      },
    })
    const {
      isOpen: isReviewDetailsDialogOpen,
      openModal: openReviewDetailsDialog,
      closeModal: closeReviewDetailsDialog,
    } = useModalState()
    const [selectedReview, setSelectedReview] = useState<ProductReview | undefined>()
    const [selectedMediaIndex, setSelectedMediaIndex] = useState<number | undefined>()

    const onThumbnailClick = useCallback(
      (reviewIndex: number, mediaIndex?: number) => {
        setSelectedReview(reviewsPhotoCarouselSource?.reviews[reviewIndex])
        setSelectedMediaIndex(mediaIndex)
        openReviewDetailsDialog()
      },
      [openReviewDetailsDialog, reviewsPhotoCarouselSource?.reviews]
    )

    const swiperAssets: ReviewMedia[] | undefined = useMemo(() => {
      if (selectedReview && selectedReview.imageUrls) {
        return selectedReview.imageUrls.map(url => ({
          src: url,
          type: isReviewMediaImage(url) ? 'image' : 'video',
        }))
      }
      return undefined
    }, [selectedReview])

    const aggregateReviewMedia = useMemo(() => {
      const output: ReviewMediaTile[] = []
      reviewsPhotoCarouselSource?.reviews.forEach(({ imageUrls }, reviewIndex) => {
        if (imageUrls) {
          // Only show the firstImage of each review for variety
          const firstImage = imageUrls[0]
          firstImage &&
            output.push({
              src: firstImage,
              type: isReviewMediaImage(firstImage) ? 'image' : 'video',
              reviewIndex,
              mediaIndex: 0,
            })
        }
      })
      return output
    }, [reviewsPhotoCarouselSource?.reviews])

    if (!productReviews || !isBrowser()) return null

    const reviewTitleText: string = `${
      productReviews?.totalReviews?.toLocaleString() ?? ''
    } ${pluralize(reviewsCountNoun, productReviews?.totalReviews ?? 20)}`

    const displayRating = parseFloat((productReviews?.averageScore ?? 0).toFixed(1)).toString()

    const tooltip = reviewStarRating.replace('{{rating}}', displayRating)

    const NoReviewComponent = (
      <ReviewWrapper ref={ref}>
        <ReviewTitle style={{ margin: '0 0 16px' }} asTag='h2'>
          {reviewsEmptyTitle}
        </ReviewTitle>
        <ReviewStarIconContainer style={{ marginBottom: '40px' }}>
          {Array.from({ length: 5 }, (_item, index) => (
            <Star key={`${index}-3`} height={28} width={28} />
          ))}
        </ReviewStarIconContainer>
        <Link aria-label={writeReviewCTA} href={reviewsMagnoliaWritePath} rel='noopener noreferrer'>
          <ButtonWrapper>
            <ReviewButton>{writeReviewCTA}</ReviewButton>
          </ButtonWrapper>
        </Link>
      </ReviewWrapper>
    )

    const ReviewButtonComponent = () => {
      return (
        <Link
          aria-label={seeAllReviewsCTA}
          href={reviewsMagnoliaViewPath}
          rel='noopener noreferrer'
        >
          <ButtonWrapper>
            <ReviewButton
              {...trackEvent({ category: 'product details', action: 'see all reviews' })}
            >
              <ButtonText>{seeAllReviewsCTA}</ButtonText>
            </ReviewButton>
          </ButtonWrapper>
        </Link>
      )
    }

    const hasReviews = productReviews.reviews.length > 0
    if (!hasReviews) return NoReviewComponent

    return (
      <NextDialogProvider open={isReviewDetailsDialogOpen}>
        <ReviewBackground>
          <PaddedItem {...{ ref }} id='reviews-padded-item' />
          <ReviewWrapper>
            <ReviewRatingsContainer>
              <ReviewHeader>
                <ReviewTitle asTag='h2'>{reviewsTitle}</ReviewTitle>
                <ReviewTitleButtonWrapper>
                  <ReviewButtonComponent />
                </ReviewTitleButtonWrapper>
              </ReviewHeader>
              <Divider />
              <ReviewAverageScore>{displayRating}/5</ReviewAverageScore>
              <ReviewStarIconContainer>
                <ReviewStars
                  aria-label={tooltip}
                  rating={productReviews?.averageScore || 5}
                  title={tooltip}
                  isLarge
                />
              </ReviewStarIconContainer>
              <ReviewCountTextContainerBody>
                <ProductDetailsCopy>
                  <ReviewCountText>
                    {reviewBaseOn} {reviewTitleText}
                  </ReviewCountText>
                </ProductDetailsCopy>
              </ReviewCountTextContainerBody>
              {productReviews.totalReviews &&
              productReviews.reviewSummary &&
              productReviews.reviewSummary.length > 0 ? (
                <ReviewSummary
                  reviewSummary={productReviews.reviewSummary}
                  totalReviewCount={productReviews.totalReviews}
                />
              ) : null}
            </ReviewRatingsContainer>
            {aggregateReviewMedia.length > 0 ? (
              <ReviewPhotosWrapper>
                <ReviewPhotosSection
                  tiles={aggregateReviewMedia}
                  tileWidthMobile={104}
                  tileWidthDesktop={150.5}
                  tileGapMobile={8}
                  tileGapDesktop={10.5}
                  titleText={reviewPhotosTitle}
                  renderTile={(tile: ReviewMediaTile, index: number) => (
                    <ReviewDialog.ThumbnailTarget
                      onClick={() => onThumbnailClick(tile.reviewIndex, tile.mediaIndex)}
                      render={
                        <Focusable>
                          <MediaThumbnail
                            src={tile.src}
                            isVideo={tile.type === 'video'}
                            loading='lazy'
                            alt={`Review Media Thumbnail ${index + 1}`}
                            aspectRatios={{ sm: 0.75, md: 0.75 }}
                            widths={{ unit: 'px', sm: 96, md: 140 }}
                          />
                        </Focusable>
                      }
                    />
                  )}
                />
              </ReviewPhotosWrapper>
            ) : null}
            <ButtonWrapperMobile>
              <ReviewButtonComponent />
            </ButtonWrapperMobile>
            {productReviews.reviews.map((review, reviewIndex) => (
              <ReviewItem key={`${reviewIndex}-${review.reviewer}`}>
                <Review.Card isPDP={true}>
                  <Review.ReviewContent>
                    <Review.Header>
                      <StarRating rating={review.score} starSize='16px' />
                      {review.createdAt ? (
                        <Review.CreatedAtDate asTag='span'>
                          {format(review.createdAt, 'MMMM d, yyyy')}
                        </Review.CreatedAtDate>
                      ) : null}
                    </Review.Header>
                    <Review.Title asTag='h2'>{review.title}</Review.Title>
                    <Review.BodyText>
                      <ExpandableText
                        content={review.content}
                        truncateLength={260}
                        readMoreText={readMore}
                        readLessText={readLess}
                      />
                    </Review.BodyText>
                    {review.imageUrls?.length ? (
                      <Review.ThumbnailsSection>
                        {review.imageUrls.map((url, urlIndex) => {
                          if (isSmallViewport && urlIndex > 1) return null
                          const isImage =
                            /\.(jpg|jpeg|png|gif|bmp|webp|svg|tiff?|ico|apng|avif|heif|heic)(\?[^ ]*)?$/.test(
                              url
                            )
                          return (
                            <Review.ThumbnailWrapper key={url}>
                              <ReviewDialog.ThumbnailTarget
                                onClick={() => onThumbnailClick(reviewIndex, urlIndex)}
                                render={
                                  <Focusable>
                                    <MediaThumbnail
                                      src={url}
                                      alt={`Review Media Thumbnail ${urlIndex + 1}`}
                                      isVideo={!isImage}
                                      aspectRatios={{ sm: 0.75, md: 0.75 }}
                                      loading='default'
                                      widths={{ unit: 'px', sm: 96, md: 66 }}
                                      {...trackEvent({
                                        category: 'review highlights',
                                        action: 'click tile',
                                        value: 'image',
                                      })}
                                    />
                                  </Focusable>
                                }
                              />
                            </Review.ThumbnailWrapper>
                          )
                        })}
                        {review.imageUrls.length - 2 > 0 ? (
                          <Review.ThumbnailsOverflow>
                            {`+ ${review.imageUrls.length - 2}`}
                          </Review.ThumbnailsOverflow>
                        ) : null}
                      </Review.ThumbnailsSection>
                    ) : null}
                  </Review.ReviewContent>
                  {review.customFields && review.customFields.length > 0 ? (
                    <Review.PurchaseDetailsWrapper hasContent>
                      <Review.PurchaseDetails>
                        <Review.ReviewerName>{review.reviewer}</Review.ReviewerName>
                        <Review.DetailsList>
                          {review.customFields
                            .map(({ key, value }) => {
                              const field = fieldsByFieldId[key!]
                              return {
                                id: field?.fieldId,
                                name: field?.name,
                                value: field ? getFieldValue(field, value!) : undefined,
                              }
                            })
                            .map(({ id, name, value }) => {
                              if (!id || !name || !value) return null

                              const isNameValidKey = isReviewsTranslatedFilterWithValuesKey(name)

                              const localizedName = isNameValidKey
                                ? filterFixtures[name].title
                                : undefined

                              const localizedValue = isNameValidKey
                                ? getTranslatedValueFromReviewsFilter({
                                    valueKey: value,
                                    filter: filterFixtures[name],
                                  })
                                : undefined

                              return (
                                <Review.DetailsItem key={id}>
                                  <b>{localizedName ?? name}:</b> {localizedValue ?? value}
                                </Review.DetailsItem>
                              )
                            })}
                        </Review.DetailsList>
                      </Review.PurchaseDetails>
                    </Review.PurchaseDetailsWrapper>
                  ) : null}
                </Review.Card>
              </ReviewItem>
            ))}
            <ButtonWrapperFooter>
              <ReviewButtonComponent />
            </ButtonWrapperFooter>
          </ReviewWrapper>
          <ReviewDialog.Dialog onClose={closeReviewDetailsDialog}>
            <ReviewDialog.DialogContent>
              <ReviewDialog.CloseButtonWrapper displayOnMobile={true}>
                <ReviewDialog.CloseButton
                  onClick={closeReviewDetailsDialog}
                  aria-label={navigation.closeButtonLabel}
                  {...trackEvent({ category: 'reviews modal', action: 'close' })}
                >
                  <SelectMark width='16px' height='16px' strokeWidth={0.75} />
                </ReviewDialog.CloseButton>
              </ReviewDialog.CloseButtonWrapper>
              <ReviewDialog.SwiperWrapper>
                {swiperAssets && swiperAssets.length > 0 ? (
                  <ReviewsSwiperMediaSlider
                    sliderAssets={swiperAssets}
                    initialSlideIndex={selectedMediaIndex}
                  />
                ) : null}
              </ReviewDialog.SwiperWrapper>
              <ReviewDialog.ReviewWrapper>
                <ReviewDialog.CloseButtonWrapper displayOnMobile={false}>
                  <ReviewDialog.CloseButton
                    onClick={closeReviewDetailsDialog}
                    aria-label={navigation.closeButtonLabel}
                    {...trackEvent({ category: 'reviews modal', action: 'close' })}
                  >
                    <SelectMark width='16px' height='16px' strokeWidth={0.75} />
                  </ReviewDialog.CloseButton>
                </ReviewDialog.CloseButtonWrapper>
                {selectedReview ? (
                  <ReviewDialog.ReviewContent>
                    <ReviewDialog.Header>
                      <StarRating rating={selectedReview.score} starSize='16px' />
                      {selectedReview.createdAt ? (
                        <ReviewDialog.CreatedAtDate asTag='span'>
                          {format(selectedReview.createdAt, 'MMMM d, yyyy')}
                        </ReviewDialog.CreatedAtDate>
                      ) : null}
                    </ReviewDialog.Header>
                    <ReviewDialog.Title asTag='h2'>{selectedReview.title}</ReviewDialog.Title>
                    <ReviewDialog.BodyText>
                      <ExpandableText
                        content={selectedReview.content}
                        truncateLength={260}
                        readMoreText={readMore}
                        readLessText={readLess}
                      />
                    </ReviewDialog.BodyText>
                    {selectedReview.customFields && selectedReview.customFields.length > 0 ? (
                      <ReviewDialog.PurchaseDetailsWrapper hasContent>
                        <ReviewDialog.PurchaseDetails>
                          <ReviewDialog.ReviewerName>
                            {selectedReview.reviewer}
                          </ReviewDialog.ReviewerName>
                          <ReviewDialog.DetailsList>
                            {selectedReview.customFields
                              .map(({ key, value }) => {
                                const field = fieldsByFieldId[key!]
                                return {
                                  field,
                                  value: field ? getFieldValue(field, value!) : undefined,
                                }
                              })
                              .filter(({ field, value }) => field && value)
                              .map(({ field, value }) => (
                                <ReviewDialog.DetailsItem key={field!.fieldId}>
                                  <b>{field!.name}:</b> {value}
                                </ReviewDialog.DetailsItem>
                              ))}
                          </ReviewDialog.DetailsList>
                        </ReviewDialog.PurchaseDetails>
                      </ReviewDialog.PurchaseDetailsWrapper>
                    ) : null}
                  </ReviewDialog.ReviewContent>
                ) : null}
              </ReviewDialog.ReviewWrapper>
            </ReviewDialog.DialogContent>
          </ReviewDialog.Dialog>
        </ReviewBackground>
      </NextDialogProvider>
    )
  }
)
